/**
 * Shared utilities for {@link ProgramRow}, {@link ProgramCard} and {@link ProgramsDataTable}.
 */
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { Program } from '@/types/program.types';
import type { ProgramFilters, ProgramType } from './program-configs';

/**
 * Returns a formatted date range string (e.g. "01/09/2025 - 30/06/2026"),
 * or null if start or end date is missing.
 */
export function formatProgramDateRange(program: Program): string | null {
  if (!program.startDate || !program.endDate) return null;
  const start = format(new Date(program.startDate), 'dd/MM/yyyy', { locale: fr });
  const end = format(new Date(program.endDate), 'dd/MM/yyyy', { locale: fr });
  return `${start} - ${end}`;
}

/** Selected types ∩ mode types, or all mode types when none are selected. */
export function getEffectiveProgramTypes(
  filters: ProgramFilters,
  modeTypes: ProgramType[],
): ProgramType[] {
  return filters.types.length > 0 ? modeTypes.filter((t) => filters.types.includes(t)) : modeTypes;
}

/** Map UI filters + pagination to program list params. */
export function toProgramListParams(
  filters: ProgramFilters,
  page: number,
  pageSize: number,
  modeTypes: ProgramType[],
): {
  page: number;
  pageSize: number;
  statuses?: ProgramFilters['statuses'];
  types: ProgramType[];
} {
  return {
    page,
    pageSize,
    statuses: filters.statuses.length > 0 ? filters.statuses : undefined,
    types: getEffectiveProgramTypes(filters, modeTypes),
  };
}

/** True when any status or type chip is set. */
export function hasProgramActiveFilters(filters: ProgramFilters): boolean {
  return filters.statuses.length > 0 || filters.types.length > 0;
}
